import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { Send, ArrowLeft, PenSquare, Search, X, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function AdminMessages() {
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [picking, setPicking] = useState(false);
  const [athletes, setAthletes] = useState([]);
  const [q, setQ] = useState("");
  const endRef = useRef(null);

  const loadThreads = () => api.get("/admin/messages/threads").then(r => setThreads(r.data));
  useEffect(() => { loadThreads(); }, []);

  const openThread = async (t) => {
    setActive(t);
    setPicking(false);
    const r = await api.get(`/admin/messages/${t.user_id}`);
    setMsgs(r.data);
  };

  const openPicker = async () => {
    setPicking(true);
    setQ("");
    if (athletes.length === 0) {
      try {
        const r = await api.get("/admin/athletes");
        const list = Array.isArray(r.data) ? r.data : (r.data.athletes || []);
        setAthletes(list);
      } catch { toast.error("Could not load runners"); }
    }
  };

  const startWith = async (a) => {
    const t = { user_id: a.id, name: a.name, email: a.email };
    await openThread(t);
  };

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return athletes;
    return athletes.filter(a =>
      (a.name || "").toLowerCase().includes(s) || (a.email || "").toLowerCase().includes(s)
    );
  }, [athletes, q]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async (e) => {
    e?.preventDefault();
    if (!text.trim() || !active || busy) return;
    setBusy(true);
    try {
      await api.post(`/admin/messages/${active.user_id}`, { body: text.trim() });
      setText("");
      const r = await api.get(`/admin/messages/${active.user_id}`);
      setMsgs(r.data);
      loadThreads();
    } catch { toast.error("Send failed"); } finally { setBusy(false); }
  };

  const deleteMsg = async (mid) => {
    try {
      await api.delete(`/admin/messages/${active.user_id}/${mid}`);
      setMsgs(prev => prev.filter(m => m.id !== mid));
      loadThreads();
      toast.success("Message deleted");
    } catch { toast.error("Delete failed"); }
  };

  const deleteThread = async () => {
    if (!active) return;
    if (!window.confirm(`Delete the entire conversation with ${active.name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/messages/${active.user_id}`);
      toast.success("Conversation deleted");
      setActive(null);
      setMsgs([]);
      loadThreads();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="admin-messages">
      <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#d7ff00]">DIRECT MESSAGES</div>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="font-display text-4xl uppercase">Athlete <span className="text-[#d7ff00]">inbox</span></h1>
        <button
          onClick={openPicker}
          className="inline-flex items-center gap-2 bg-[#d7ff00] text-black font-display tracking-wider uppercase text-sm px-4 py-2 hover:bg-white"
          data-testid="new-message-btn"
        >
          <PenSquare className="w-4 h-4" /> New message
        </button>
      </div>

      <div className="mt-6 grid md:grid-cols-[300px_1fr] gap-4 min-h-[60vh]">
        <div className={`bg-[#0a0a0a] border border-[#1f1f1f] divide-y divide-[#1f1f1f] ${active ? "hidden md:block" : ""}`}>
          {threads.length === 0 && <div className="p-4 text-[#888] text-sm">No threads yet. Hit <span className="text-[#d7ff00]">New message</span> to reach any runner.</div>}
          {threads.map(t => (
            <button key={t.user_id} onClick={() => openThread(t)} className={`w-full text-left p-3 hover:bg-[#111] ${active?.user_id === t.user_id ? "bg-[#111]" : ""}`} data-testid={`thread-${t.user_id}`}>
              <div className="flex justify-between">
                <div className="font-display uppercase text-sm">{t.name}</div>
                <div className="font-mono-jp text-[9px] text-[#666] uppercase tracking-widest">{new Date(t.last_at).toLocaleDateString()}</div>
              </div>
              <div className="text-xs text-[#888] truncate">{t.last_role === "coach" ? "You: " : ""}{t.last_body}</div>
            </button>
          ))}
        </div>

        <div className={`bg-[#0a0a0a] border border-[#1f1f1f] flex flex-col ${!active ? "hidden md:flex" : "flex"}`}>
          {!active ? (
            <div className="m-auto text-[#888] text-sm">Select a thread, or hit <span className="text-[#d7ff00]">New message</span>.</div>
          ) : (
            <>
              <div className="p-3 border-b border-[#1f1f1f] flex items-center gap-2">
                <button className="md:hidden text-[#888]" onClick={() => setActive(null)} data-testid="back-threads"><ArrowLeft className="w-4 h-4" /></button>
                <div className="font-display uppercase">{active.name}</div>
                <div className="font-mono-jp text-[10px] text-[#888] uppercase tracking-widest">{active.email}</div>
                <button
                  onClick={deleteThread}
                  title="Delete entire conversation"
                  className="ml-auto text-[#666] hover:text-[#ff5470] p-1"
                  data-testid="delete-thread-btn"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="admin-thread-msgs">
                {msgs.length === 0 && <div className="text-[#666] text-xs font-mono-jp uppercase tracking-widest">No messages yet — say hi 👋</div>}
                {msgs.map(m => (
                  <div key={m.id} className={`group flex items-center gap-2 ${m.from_role === "coach" ? "justify-end" : "justify-start"}`}>
                    {m.from_role === "coach" && (
                      <button
                        onClick={() => deleteMsg(m.id)}
                        title="Delete message"
                        className="opacity-0 group-hover:opacity-100 transition-opacity text-[#666] hover:text-[#ff5470]"
                        data-testid={`delete-msg-${m.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <div className={`max-w-[80%] px-4 py-2.5 text-sm leading-relaxed ${m.from_role === "coach" ? "bg-[#d7ff00] text-black" : "bg-[#161616] text-[#eee] border border-[#222]"}`}>
                      {m.body}
                    </div>
                    {m.from_role !== "coach" && (
                      <button
                        onClick={() => deleteMsg(m.id)}
                        title="Delete message"
                        className="opacity-0 group-hover:opacity-100 transition-opacity text-[#666] hover:text-[#ff5470]"
                        data-testid={`delete-msg-${m.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                ))}
                <div ref={endRef} />
              </div>
              <form onSubmit={send} className="p-3 border-t border-[#1f1f1f] flex gap-2">
                <input value={text} onChange={e => setText(e.target.value)} placeholder="Message as Coach JP…" disabled={busy}
                  className="flex-1 bg-[#0e0e0e] border border-[#222] focus:border-[#d7ff00] outline-none px-4 py-2.5 text-white" data-testid="admin-reply-input" />
                <button type="submit" disabled={busy || !text.trim()} className="bg-[#d7ff00] text-black px-4 hover:bg-white disabled:opacity-40" data-testid="admin-reply-send">
                  <Send className="w-4 h-4" />
                </button>
              </form>
            </>
          )}
        </div>
      </div>

      {picking && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center p-4 sm:p-10" data-testid="runner-picker-overlay" onClick={() => setPicking(false)}>
          <div className="bg-[#0a0a0a] border border-[#1f1f1f] w-full max-w-lg mt-10 max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-[#1f1f1f] flex items-center justify-between">
              <div className="font-display uppercase text-lg">Pick a runner</div>
              <button onClick={() => setPicking(false)} className="text-[#888] hover:text-white" data-testid="close-picker"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-3 border-b border-[#1f1f1f] flex items-center gap-2">
              <Search className="w-4 h-4 text-[#666]" />
              <input
                autoFocus value={q} onChange={e => setQ(e.target.value)}
                placeholder="Search by name or email…"
                className="flex-1 bg-transparent outline-none text-white text-sm"
                data-testid="runner-search-input"
              />
            </div>
            <div className="overflow-y-auto divide-y divide-[#1f1f1f]">
              {filtered.length === 0 && <div className="p-4 text-[#888] text-sm">No runners match.</div>}
              {filtered.map(a => (
                <button key={a.id} onClick={() => startWith(a)} className="w-full text-left p-3 hover:bg-[#111] flex items-center justify-between" data-testid={`pick-runner-${a.id}`}>
                  <div>
                    <div className="font-display uppercase text-sm">{a.name}</div>
                    <div className="text-xs text-[#888]">{a.email}</div>
                  </div>
                  {a.has_coaching && <span className="font-mono-jp text-[9px] uppercase tracking-widest text-black bg-gradient-to-r from-[#d7ff00] to-[#9eff00] px-2 py-0.5">Coaching</span>}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
